import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ASK_INVITE, PremiumOfferContext } from './premiumOffer';
import { useAuth } from '@clerk/react';
import { useDiffitUserContext } from './DiffitUserContext';
import {
  getChurnState,
  isInExpiryWindow,
  isPremiumExpiringSoon,
  isTrialExpiringSoon,
  isTrialRecentlyEnded,
  resolveExpiringSoonWindowDays,
} from '../utils/churnedOrg';
import { getPremiumOffer } from '../services/UserService';
import { Mixpanel } from '../services/Mixpanel';
import LoadingSpinner from '../components/LoadingSpinner';

// Loaded on open rather than imported: a static import would put both modals back into the
// graph premiumOffer.js exists to keep clean, and neither is needed until one is opened.
const ShareForPremiumModal = lazy(() => import('../components/modals/ShareForPremiumModal'));
const ShareWithAdminModal = lazy(() => import('../components/modals/ShareWithAdminModal'));

/**
 * The app's single "get 30 more days" offer, mounted once in Base.
 *
 * Every premium paygate can offer a churned teacher the extension promo code instead of an
 * individual plan, and which ask they get — invite colleagues, or ask your admin to renew —
 * depends on how many teachers at their school are actually using Diffit. That's a database
 * count, so it is fetched once here and shared, rather than per paygate.
 *
 * Both modals live here for the same reason SignInModal does: a paygate can be anywhere
 * (a sidebar, a packet overlay, a modal already on screen), and each one rendering its own
 * copy would mount several at once.
 *
 * `offered` stays false until the fetch answers, so a paygate renders its existing
 * plans link first and only swaps to the offer once we know there is one. A failed fetch
 * leaves it false forever — the paygate still sells a plan, which is the safe direction.
 */
export const PremiumOfferProvider = ({ children }) => {
  const { isSignedIn } = useAuth();
  const { diffitUser, hasActiveSubscription } = useDiffitUserContext();
  const { expiringSoonWindowDays } = useFlags();
  const windowDays = resolveExpiringSoonWindowDays(expiringSoonWindowDays);
  const [offer, setOffer] = useState({ offered: false, ask: null, redeemed: false });
  // Whether the lookup has finished, one way or another — including "we never asked". The ad
  // slots hold their card back until this is true, so an expiry ad cannot paint after a
  // lower-priority one has already taken the slot.
  const [isSettled, setIsSettled] = useState(false);
  // Which user that answer is for. isSettled is already true before diffitUser loads (nothing to
  // ask yet), so a one-shot reader like the ?modal= deep link needs to know it's THIS user's.
  const [settledUserId, setSettledUserId] = useState(null);
  // Which ask is open, and the paygate that opened it — the location rides into the
  // invite email's ref_src and onto every event this modal fires.
  const [open, setOpen] = useState(null);

  // Depended on by value, not by object identity. Every seen_nux write — dismissing an ad, a
  // sidebar card, refreshing after the extension lands — replaces `diffitUser` with a fresh
  // object for the SAME teacher, and re-running on those would unsettle an answer that cannot
  // have changed, blanking the ad slots that wait on it for a round trip.
  const userId = diffitUser?.id;
  const isExpiringSoon = isPremiumExpiringSoon(diffitUser, windowDays);
  // ...but a grant DOES change the answer (it flips `redeemed`), and redeeming the extension
  // always moves trial_end_date — so that one field re-asks, where seen_nux writes do not.
  const trialEndsAt = diffitUser?.trial_end_date_iso;

  useEffect(() => {
    // A paying teacher never sees a paygate, so the count query behind this answer is work
    // nobody reads. Trial users are not excluded — the gates do show for them.
    // Waits for diffitUser: hasActiveSubscription is false before it loads, so firing on the
    // first render would ask for every signed-in user and skip nobody.
    // A teacher whose school licence lapses next week is still `hasActiveSubscription`, so the
    // skip would hide the ask from exactly the people the expiry ads exist for. The endpoint
    // answers on school size and churn, neither of which assumes they are unpaid.
    if (!isSignedIn || !userId || (hasActiveSubscription && !isExpiringSoon)) {
      setIsSettled(true);
      setSettledUserId(userId ?? null);
      return undefined;
    }
    let cancelled = false;
    setIsSettled(false);
    setSettledUserId(null);
    getPremiumOffer()
      .then((response) => {
        // Every answer, not just positive ones: this re-asks after a grant, and an answer of
        // "no offer" must replace the earlier one rather than leave its 30 days on the table.
        if (!cancelled && response.data) setOffer(response.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setIsSettled(true);
          setSettledUserId(userId);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, userId, hasActiveSubscription, isExpiringSoon, trialEndsAt]);

  // Opens whichever modal `ask` maps to, independent of what the fetched offer recommends.
  // openPremiumOffer below is the offer-driven wrapper every ordinary paygate CTA uses; the
  // ?modal= deep link (PremiumOfferDeepLink) already knows which ask it wants — it only needs
  // the eligibility check, which lives outside this provider — and calls this directly.
  const openOfferModal = useCallback(
    (ask, location) => {
      Mixpanel.track('Premium Offer Opened', {
        location,
        ask,
        has_redeemed: Boolean(offer.redeemed),
      });
      setOpen({ ask, location });
    },
    [offer.redeemed]
  );

  // The admin ask is a renewal pitch, so the paygates only make it inside the expiry window.
  // Everyone else is asked to invite colleagues — except a teacher who already spent the
  // extension, for whom the invite route earns nothing, so their paygates sell a plan instead.
  const inExpiryWindow = isInExpiryWindow(diffitUser, windowDays);
  const isOffered = Boolean(offer.offered) && (inExpiryWindow || !offer.redeemed);
  const paygateAsk = inExpiryWindow ? offer.ask : ASK_INVITE;

  const openPremiumOffer = useCallback(
    (location) => {
      if (!isOffered) return;
      openOfferModal(paygateAsk, location);
    },
    [isOffered, paygateAsk, openOfferModal]
  );

  const value = useMemo(
    () => ({
      isOffered,
      ask: offer.ask,
      isInExpiryWindow: inExpiryWindow,
      isSettled,
      isSettledForUser: isSettled && !!userId && settledUserId === userId,
      // Whether the share-with-admin endpoint would accept them — a school sender domain, not
      // a consumer one. Separate from `ask`: that says which route their school EARNS, this
      // says whether the admin route WORKS at all.
      canShareWithAdmin: Boolean(offer.can_share_with_admin),
      // The extension is already spent: the ask becomes "get your admin to buy it", and the
      // CTA says so rather than promising 30 days nobody can grant twice.
      hasRedeemed: Boolean(offer.redeemed),
      // Whether 30 days are actually on the table, which is NOT the same as being offered
      // something. The invite ask pays through the referral program either way, but the
      // admin ask only grants the extension for a churned school — so a free school's
      // teacher gets the celebration modal, which promises nothing, and must not be sent
      // there by a CTA promising 30 days.
      // Reads the churn *state*, not the org's name: names are nullable, and the backend
      // grants the extension on dates. Keying off the name would drop the offer for a
      // churned teacher whose org has none, while the trial was extended anyway.
      // `redeemed` implies the admin ask (the endpoint forces it), so the invite branch
      // below is never reached in that state.
      // A teacher on their own expiring trial is the third case that pays: they are not paid,
      // so the promo code applies and simply pushes trial_end_date out. Mirrors the grant
      // condition in diffit-api's share-with-admin router — these two must not drift, or the
      // card promises days the send does not hand over.
      offersDays:
        Boolean(offer.offered) &&
        !offer.redeemed &&
        (offer.ask === ASK_INVITE ||
          getChurnState(diffitUser).state === 'recent' ||
          isTrialExpiringSoon(diffitUser, windowDays) ||
          isTrialRecentlyEnded(diffitUser)),
      openPremiumOffer,
      openPremiumOfferModal: openOfferModal,
    }),
    [
      offer,
      isOffered,
      inExpiryWindow,
      diffitUser,
      isSettled,
      userId,
      settledUserId,
      openPremiumOffer,
      openOfferModal,
      windowDays,
    ]
  );

  return (
    <PremiumOfferContext.Provider value={value}>
      {children}
      {open && (
        <Suspense
          fallback={
            // The chunk is fetched on the click that opens it; on a slow connection an
            // empty fallback would read as a dead button.
            <div className='fixed inset-0 z-[60] flex items-center justify-center bg-gray-500 bg-opacity-75'>
              <LoadingSpinner />
            </div>
          }>
          {open.ask === ASK_INVITE ? (
            <ShareForPremiumModal
              showModal
              setShowModal={() => setOpen(null)}
              location={open.location}
            />
          ) : (
            <ShareWithAdminModal
              showModal
              setShowModal={() => setOpen(null)}
              location={open.location}
            />
          )}
        </Suspense>
      )}
    </PremiumOfferContext.Provider>
  );
};
